import * as I from 'immutable';

import {
  ApiAccessSubscription,
  ApiOrganization,
  ApiOrganizationUser,
} from 'app/modules/Remote/Organization';
import * as CONSTANTS from 'app/utils/constants';
import {atLeastUserRole, isOwnerOrAdmin, isUpstreamEmail} from 'app/utils/userUtils';

import {orgIsAllowedTier} from './organizationUtils';

// Reexport useUserInfo to reduce imports for consumers.
export {useUserInfo} from 'app/providers/AuthProvider';
export const UPSTREAM_TEST_ORG_ID = '-KQ66NhS23Am_QKkk7CR';
export const UPSTREAM_DEMO_ORG_ID = 'demo';
export const UPSTREAM_TEST_PARENT_ORG_ID = 'edab39da-c8a0-423a-bb5f-f5bc61d67cbd';
export const UPSTREAM_TEST_CHILD_ORG_ID = 'f92109ba-b63f-4c88-83ec-a73a2b2be097';
export const UPSTREAM_TEST_CANCEL_SUBSCRIPTION_ORG_ID = 'd5867a1d-8cf9-4343-bc25-1ea4afffcaed';

export const FLINT_RIVERKEEPER_ORG_ID = '707fd3b7-7d92-494f-9a66-002b54d27a59';
export const SPNHF_PROPERTY_MONITORING_ORG_ID = '0e4fd0c5-a574-4dee-b775-78ae8d871488';
export const PLANET_LABS_DEMO_ORG_ID = '1078146a-7950-4a53-b847-acc15ec458a4';

// the primary accounts for the upstream lens engineers so we can hide special little stuff
export const LENS_ENGINEER_USER_IDS = [
  'RaRrLZBs0QUuxO9bFKEJtS3mfwv2', //arohan
  'pc6BalHnVEdjHUrWZQIwAJ0NIW12', //anna
  'BDArLBr3T8Y71RgEYK9jie3mzBc2', //dan
  'slvniQK80TOgWajCMbG0tCLcFJG2', //eva
  'v6XqaKCHkOfAcpV52JCoDZX93Mz1', //maya
  'N3JN1awV1kcnVW82zjqOdDiOsFo2', //marsh
  'pNtumFpLpARPKMRXlGRFA7Rv3yZ2', //miles
  'AHrEo2cZWzcMmYHrKDG9u9VeSTa2', //abby
  '67MEY5h8bKfjK35L4Pkibs4M3Y43', //katie
  'R3TL92MywFNe20HyVrldLw1lnwq1', //eve
];

/**
 * Returns true if the user has an Upstream email, and the organization is
 * one of the designated internal organizations.
 */

export function isUpstreamAndInternalOrg(
  organization: I.ImmutableOf<ApiOrganization> | null,
  profile: I.ImmutableOf<ApiOrganizationUser> | null
): boolean {
  // Auth provider may return null for these if we're not logged in. Return false here, conservatively.
  if (!organization || !profile) {
    return false;
  }
  return (
    isUpstreamEmail(profile.get('email')) &&
    [UPSTREAM_TEST_ORG_ID, UPSTREAM_TEST_PARENT_ORG_ID, UPSTREAM_TEST_CHILD_ORG_ID].includes(
      organization.get('id')
    )
  );
}

/**
 * Returns true if the user has an Upstream email, and the organization is
 * one of the designated internal organizations or the demo org.
 */

export function isUpstreamAndInternalOrDemoOrg(
  organization: I.ImmutableOf<ApiOrganization> | null,
  profile: I.ImmutableOf<ApiOrganizationUser> | null
): boolean {
  // Auth provider may return null for these if we're not logged in. Return false here, conservatively.
  if (!organization || !profile) {
    return false;
  }
  return (
    isUpstreamEmail(profile.get('email')) &&
    [
      UPSTREAM_TEST_ORG_ID,
      UPSTREAM_TEST_PARENT_ORG_ID,
      UPSTREAM_TEST_CHILD_ORG_ID,
      UPSTREAM_DEMO_ORG_ID,
    ].includes(organization.get('id'))
  );
}

/**
 * Returns true if the user has an Upstream email, and the assigned user is currently
 * a member of the internal org. This is useful for excluding service accounts that
 * may be attatched to customer orgs but still have @upstream.tech emails.
 */
export function isInternalOrgMemberViewingAnyOrg(
  profile: I.ImmutableOf<ApiOrganizationUser> | null
): boolean {
  // Auth provider may return null for these if we're not logged in. Return false here, conservatively.
  if (!profile) {
    return false;
  }
  return (
    isUpstreamEmail(profile.get('email')) &&
    profile
      .get('organizations')
      .map((userAssociation) => userAssociation?.get('id'))
      .includes(CONSTANTS.INTERNAL_ORGANIZATION_ID)
  );
}

/** Any Upstream user other than the playground user (mirrors the backend staff check). */
export function isUpstreamStaff(profile: I.ImmutableOf<ApiOrganizationUser> | null): boolean {
  if (!profile) {
    return false;
  }
  return isUpstreamEmail(profile.get('email')) && !isLensPlaygroundUser(profile);
}

export function isUpstreamEngineer(profile: I.ImmutableOf<ApiOrganizationUser> | null): boolean {
  // Auth provider may return null for these if we're not logged in. Return false here, conservatively.
  if (!profile) {
    return false;
  }
  return LENS_ENGINEER_USER_IDS.includes(profile.get('id'));
}

export function isLensPlaygroundUser(profile: I.ImmutableOf<ApiOrganizationUser> | null): boolean {
  return profile?.get('email') === CONSTANTS.LENS_PLAYGROUND_USER_ID;
}

export default {
  /************************************
   * FEATURES NOT RELEASED TO USERS YET
   ************************************/
  AGENT: (
    _organization: I.ImmutableOf<ApiOrganization>,
    profile: I.ImmutableOf<ApiOrganizationUser>
  ) => isUpstreamStaff(profile),
  AGENT_IN_NAVBAR: (
    _organization: I.ImmutableOf<ApiOrganization>,
    profile: I.ImmutableOf<ApiOrganizationUser>
  ) => isUpstreamStaff(profile),
  AGENT_SIDEBAR: (
    organization: I.ImmutableOf<ApiOrganization>,
    _profile: I.ImmutableOf<ApiOrganizationUser>
  ) =>
    // if an org explicitly hides the agent, do not show it in the map sidebar
    !organization.getIn(['settings', CONSTANTS.ORG_SETTING_AGENT_SETTINGS, 'hidden']),
  AGENT_RUN_CREATION: (
    _organization: I.ImmutableOf<ApiOrganization>,
    profile: I.ImmutableOf<ApiOrganizationUser>
    // Staff can create runs in any org; backend still enforces the role check.
  ) => atLeastUserRole(CONSTANTS.USER_ROLE_REGULAR, profile),
  AGENT_ONBOARDING: (
    _organization: I.ImmutableOf<ApiOrganization>,
    profile: I.ImmutableOf<ApiOrganizationUser>
  ) => isUpstreamStaff(profile),
  // "Find more like this": draw a few example boxes, SAM finds every match on the property.
  AGENT_FEW_SHOT: (
    _organization: I.ImmutableOf<ApiOrganization>,
    profile: I.ImmutableOf<ApiOrganizationUser>
  ) => isUpstreamStaff(profile),
  AGENT_CHAT: (
    _organization: I.ImmutableOf<ApiOrganization>,
    profile: I.ImmutableOf<ApiOrganizationUser>
  ) => isInternalOrgMemberViewingAnyOrg(profile),
  // Everything on the Automations page starts or reopens a chat, so it has the chat's audience.
  AUTOMATIONS: (
    organization: I.ImmutableOf<ApiOrganization>,
    profile: I.ImmutableOf<ApiOrganizationUser>
  ) => isUpstreamAndInternalOrDemoOrg(organization, profile),
  // Composite lookouts are internal-only while they're being evaluated; the backend refuses
  // creation outside the internal org too.
  COMPOSITE_LOOKOUTS: (
    organization: I.ImmutableOf<ApiOrganization> | null,
    profile: I.ImmutableOf<ApiOrganizationUser> | null
  ) => isUpstreamAndInternalOrg(organization, profile),
  PORTFOLIO_MAP_VIEW: (
    _organization: I.ImmutableOf<ApiOrganization>,
    _profile: I.ImmutableOf<ApiOrganizationUser>
  ) => true,
  IMAGERY_CART: (
    _organization: I.ImmutableOf<ApiOrganization> | null,
    _profile: I.ImmutableOf<ApiOrganizationUser> | null
  ) => true,
  MULTISPECTRAL_ORDERS: (_profile: I.ImmutableOf<ApiOrganizationUser>) => true,
  // Allow internal users to upload GeoJSON files to the map and view them as layers
  QUICK_MAP_VECTOR_VIEWER: (profile: I.ImmutableOf<ApiOrganizationUser>) =>
    isInternalOrgMemberViewingAnyOrg(profile),
  // The map's imagery timeline player, for internal users in any org.
  IMAGERY_TIMELINE: (profile: I.ImmutableOf<ApiOrganizationUser>) =>
    isInternalOrgMemberViewingAnyOrg(profile),
  /************************************
   * RELEASED FEATURES GATED BY ROLE OR LENS TIER
   ************************************/
  // The Regrid parcel-change alerts are the one Standard feature Lite doesn't include. The
  // backend drops the enrollment for other tiers, so the controls are hidden instead of failing
  // silently.
  PARCEL_CHANGE_ALERTS: (organization: I.ImmutableOf<ApiOrganization>) =>
    orgIsAllowedTier(organization, [CONSTANTS.LENS_TIER_PLUS, CONSTANTS.LENS_TIER_ENTERPRISE]),
  ORGANIZATION_LOGO_UPLOAD: (
    _organization: I.ImmutableOf<ApiOrganization>,
    profile: I.ImmutableOf<ApiOrganizationUser>
  ) => isOwnerOrAdmin(profile.get('role')),
  TASK_IMAGERY_MODAL_SUBMIT: (
    _organization: I.ImmutableOf<ApiOrganization>,
    profile: I.ImmutableOf<ApiOrganizationUser>
  ) => atLeastUserRole(CONSTANTS.USER_ROLE_REGULAR, profile),

  // Show the cancellation link to members (read-only members never see it) in
  // non-enterprise orgs. Enterprise plans are cancelled through a separate flow,
  // so the link is hidden entirely for them.
  SHOW_CANCELLATION_LINK: (
    organization: I.ImmutableOf<ApiOrganization>,
    profile: I.ImmutableOf<ApiOrganizationUser>,
    accessSubscription: ApiAccessSubscription
  ) =>
    atLeastUserRole(CONSTANTS.USER_ROLE_REGULAR, profile) &&
    organization.getIn(['tiers', 'Lens']) !== CONSTANTS.LENS_TIER_ENTERPRISE &&
    accessSubscription.productKey !== 'lens-plus-enterprise',

  // Only org owners can cancel a subscription. Non-owners see the link disabled
  // with a tooltip directing them to an owner or the Lens team.
  CAN_CANCEL_SUBSCRIPTION: (profile: I.ImmutableOf<ApiOrganizationUser>) =>
    profile.get('role') === CONSTANTS.USER_ROLE_OWNER,
};
